import React, { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import {
    BarChartOutlined,
    FundOutlined, ReconciliationOutlined,
    LoginOutlined, MenuOutlined, MessageOutlined, PieChartOutlined,
    SearchOutlined, TeamOutlined, TruckOutlined, WhatsAppOutlined, WalletOutlined,
    UserSwitchOutlined, RobotOutlined, UserOutlined, BankOutlined, ShoppingCartOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import { ApprovalCenter } from './approvals/ApprovalCenter';
import SettingsModal from './SettingsModal/SettingsModal';
import UserSettingsModal from './SettingsModal/UserSettingsModal';
import { Avatar, Badge, Button, ConfigProvider, Dropdown, Layout, Menu, Space, Spin, theme, Tooltip as AntTooltip } from 'antd';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from './contexts/AuthContext'; // Updated import
import { useWatiChat } from './contexts/WatiChatContext';
import { useWatiSocket } from './contexts/WatiSocketContext';
import { useSocket } from './contexts/SocketContext';
import { API_DOMAIN, resolvePfpUrl } from './constants';
import { _fetchAgents, getAgentColor, agentInitials } from './qv4/helpers';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import useSnackbar from "../components/Snackbar/UseSnackbar";
import useLogout from './Login/Logout';
import siteConfig from '../siteConfig';
import uzdLogo from '../assets/images/uzd_logo_small.jpeg';
import InternalChatLauncher from './internalChat/InternalChatLauncher';
import AppSplash from './AppSplash';
import { useAccess } from '../features/access/context/AccessProvider';
import { PAGE_ROUTES, PERMISSIONS } from '../features/access/constants/permissionKeys';
import { useAccountsNotifications } from './payments-v2/contexts/AccountsNotificationsContext';

const { Header, Content, Footer, Sider } = Layout;

// Brand block at the top of the sider — logo on a white plate (artwork has a
// white background), wordmark hidden when collapsed
const SiderBrand = ({ collapsed }) => (
    <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: collapsed ? "16px 0" : "16px 16px",
        justifyContent: collapsed ? "center" : "flex-start",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        marginBottom: 8,
    }}>
        <div style={{
            background: "#fff", borderRadius: 8, padding: "4px 6px", flexShrink: 0,
            boxShadow: "0 2px 10px rgba(225,29,46,0.3)",
        }}>
            <img src={uzdLogo} alt="UZD" style={{ height: 22, display: "block", objectFit: "contain" }} />
        </div>
        {!collapsed && (
            <div style={{ lineHeight: 1.15 }}>
                <div style={{ color: "#f1f5f9", fontWeight: 800, fontSize: 13, letterSpacing: 1.5 }}>UZDPART</div>
                <div style={{ color: "#e11d2e", fontWeight: 700, fontSize: 10, letterSpacing: 3 }}>SRM</div>
            </div>
        )}
    </div>
);

function getItem(label, key, icon, path) {
    return { key, icon, label: <Link to={path}>{label}</Link>, path, title: label };
}

if (!document.getElementById("drawer-credit-style")) {
    const s = document.createElement("style");
    s.id = "drawer-credit-style";
    s.textContent = `
        @keyframes credit-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes credit-pulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
    `;
    document.head.appendChild(s);
}

const DrawerNew = () => {
    const { handledialogueOpen, LogoutDialog } = useLogout();
    const { SuccessSnackbar, ErrorSnackbar, InfoSnackbar, WarningSnackbar, ToastContainer } = useSnackbar();

    // Use the new AuthContext hook
    const { authState, legacyAuthState } = useAuth();
    const { can, status: accessStatus } = useAccess();
    const { totalUnread, qv4TotalUnread } = useWatiChat();
    const { isWatiConnected, relayStatus } = useWatiSocket();
    const { isConnected: isSrmSocketConnected, isNetworkOnline, watiPresence } = useSocket();
    const { pendingCount: accountsPendingCount } = useAccountsNotifications();

    const [templateCredit, setTemplateCredit] = useState(null);
    const [creditLoading, setCreditLoading] = useState(false);
    // Read-only diagnostic list, same agent data (and ONLINE/OFFLINE split)
    // the per-chat assign dropdown uses — but always fetched fresh
    // (_fetchAgents(true), bypassing that dropdown's cache) since the whole
    // point is spotting a live mismatch between "SRM says I'm online in
    // WATI" and what WATI's own agent list actually reports.
    const [presenceAgents, setPresenceAgents] = useState([]);
    const [presenceAgentsLoading, setPresenceAgentsLoading] = useState(false);
    const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);
    const [userSettingsOpen, setUserSettingsOpen] = useState(false);
    const [pageDrawerWidth, setPageDrawerWidth] = useState(0);
    const relayIndicator = !isSrmSocketConnected
        ? { color: "#ff4d4f", pulse: isNetworkOnline }
        : ({
        connected: { color: "#52c41a", pulse: false },
        connecting: { color: "#faad14", pulse: true },
        reconnecting: { color: "#faad14", pulse: true },
        degraded: { color: "#fa8c16", pulse: true },
        disconnected: { color: "#ff4d4f", pulse: false },
    }[relayStatus?.status] || { color: "#8c8c8c", pulse: false });

    const fetchCredit = () => {
        setCreditLoading(true);
        axios.get(API_DOMAIN + "/wati_messages/get_credits_status", {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then(res => {
            const payload = res.data?.data ?? res.data;
            const val = payload?.creditCustomer?.credit ?? null;
            setTemplateCredit(val);
        }).catch(() => {}).finally(() => setCreditLoading(false));
    };

    useEffect(() => {
        fetchCredit();
        const interval = setInterval(fetchCredit, 2 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);
    const location = useLocation();
    const navigate = useNavigate();
    const isQV4 = location.pathname === "/" || location.pathname === "/querysetv4" || location.pathname.startsWith("/operations-v2") || location.pathname === "/my-purchases" || location.pathname === "/my-deliveries" || location.pathname === "/whatsapp_messages" || location.pathname === "/ai_assistant";

    // For backward compatibility, use legacyAuthState if available
    const userState = legacyAuthState || authState;

    function stringAvatar(name) {
        if (!name) return "UU";
        if (name.split(' ').length === 2) {
            return `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`;
        } else {
            return `${name.split(' ')[0][0].toUpperCase()}`;
        }
    }

    // Define menu items - use isAuthenticated from new authState
    const navIcons = {
        dashboard: <PieChartOutlined />, "legacy-sales": <WhatsAppOutlined />,
        sales: <Badge count={qv4TotalUnread} size="small" offset={[10, 10]}><FundOutlined /></Badge>,
        "supplier-chats": <Badge count={totalUnread} size="small" offset={[10, 10]}><MessageOutlined /></Badge>,
        operations: <ReconciliationOutlined />, "my-purchases": <ShoppingCartOutlined />, "my-deliveries": <TruckOutlined />, accounts: <Badge count={accountsPendingCount} size="small" offset={[10, 10]}><BankOutlined /></Badge>, ai: <RobotOutlined />, suppliers: <TruckOutlined />,
        users: <TeamOutlined />, "access-management": <SafetyCertificateOutlined />, reports: <BarChartOutlined />, "price-search": <SearchOutlined />, customers: <UserOutlined />,
    };
    const items = authState.isAuthenticated && accessStatus === "ready"
        ? PAGE_ROUTES.filter(route => route.nav && can(route.permission)).map(route => getItem(route.label, route.key, navIcons[route.key], route.path))
        : [];

    const isMobile = useIsMobile();
    const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);
    const [selectedKey, setSelectedKey] = useState('');
    const [currentHeader, setCurrentHeader] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Start as false since auth is handled by App

    // Auto-collapse on desktop after 2.5s; mobile starts collapsed already
    useEffect(() => {
        if (isMobile) return;
        const t = setTimeout(() => setCollapsed(true), 2500);
        return () => clearTimeout(t);
    }, [isMobile]);

    // Set menu and header based on current location
    useEffect(() => {
        if (!authState.isAuthenticated) return;

        const setInitialMenuState = () => {
            // Find exact match first
            let matchedItem = items.find(item =>
                location.pathname === item.path
            );

            // If no exact match, try to match path prefix
            if (!matchedItem) {
                matchedItem = items.find(item =>
                    location.pathname.startsWith(item.path)
                );
            }

            // Special case for reports
            if (!matchedItem && location.pathname.startsWith('/report')) {
                matchedItem = items.find(item => item.path === '/reports');
            }

            if (matchedItem) {
                setSelectedKey(matchedItem.key);
                setCurrentHeader(matchedItem.title);
            } else {
                setSelectedKey('');
                setCurrentHeader('Welcome');
            }
        };

        setInitialMenuState();
    }, [location.pathname, items, authState.isAuthenticated]);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Don't show loading spinner here - auth is handled by App
    if (!authState.isAuthenticated) {
        return null; // Or a redirect component
    }
    if (accessStatus === "loading" || accessStatus === "idle") return <AppSplash />;

    const dropdown_items = [
        {
            key: '5',
            label: "User Settings",
        },
        ...(can(PERMISSIONS.SETTINGS_VIEW) ? [{
            key: '4',
            label: "Global Settings",
        }] : []),
        {
            key: '3',
            label: "Logout",
        },
    ];

    const onMenuClick = (e) => {
        switch (e.key) {
            case "5":
                setUserSettingsOpen(true);
                break;
            case "3":
                handledialogueOpen();
                break;
            case "4":
                setGlobalSettingsOpen(true);
                break;
            default:
                ErrorSnackbar(`Invalid option selected!`);
                break;
        }
    };

    return (
        <ConfigProvider theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Open Sans', 'Helvetica Neue', Arial, sans-serif",
                borderRadius: 8,
                colorBgLayout: "#f4f5f7",
                colorBorderSecondary: "#eceef1",
                boxShadowSecondary: "0 4px 16px rgba(15, 23, 42, 0.08), 0 1px 4px rgba(15, 23, 42, 0.05)",
            },
            components: {
                Layout: {
                    siderBg: "#0e1013",
                    triggerBg: "#16181d",
                    triggerColor: "rgba(255,255,255,0.55)",
                },
                Button: { fontWeight: 500 },
                Card: { borderRadiusLG: 12 },
                Modal: { borderRadiusLG: 14 },
                Drawer: { borderRadiusLG: 14 },
                Table: { headerBg: "#f7f8fa", headerSplitColor: "transparent" },
                Tag: { borderRadiusSM: 6 },
                Collapse: { headerBg: "#fafbfc", borderRadiusLG: 10 },
                Notification: { borderRadiusLG: 12 },
                Menu: {
                    darkItemBg: "#0e1013",
                    darkSubMenuItemBg: "#0e1013",
                    darkPopupBg: "#16181d",
                    darkItemColor: "rgba(255,255,255,0.62)",
                    darkItemHoverBg: "rgba(255,255,255,0.07)",
                    darkItemHoverColor: "#ffffff",
                    darkItemSelectedBg: "#e11d2e",
                    darkItemSelectedColor: "#ffffff",
                    itemBorderRadius: 8,
                    itemMarginInline: 8,
                    itemHeight: 42,
                },
            },
        }}>
            <Layout style={isQV4 ? { height: '100vh', width: '100vw', overflow: 'hidden' } : { minHeight: '100vh', minWidth: '100vw' }}>

                {/* ── Desktop: sticky collapsible sider ── */}
                {!isMobile && (
                    <Sider
                        collapsible
                        collapsed={collapsed}
                        onCollapse={setCollapsed}
                        style={{ overflow: "auto", height: "100vh", position: "sticky", top: 0, left: 0 }}
                    >
                        <SiderBrand collapsed={collapsed} />
                        <Menu theme="dark" selectedKeys={[selectedKey]} defaultOpenKeys={['pipeline-group']} mode="inline" items={items} />
                    </Sider>
                )}

                {/* ── Mobile: full-screen overlay when open ── */}
                {isMobile && !collapsed && (
                    <>
                        <div
                            onClick={() => setCollapsed(true)}
                            style={{
                                position: "fixed", inset: 0,
                                background: "rgba(0,0,0,0.5)",
                                zIndex: 1000,
                            }}
                        />
                        <Sider
                            width={230}
                            style={{
                                position: "fixed", top: 0, left: 0,
                                height: "100dvh", zIndex: 1001,
                                overflow: "auto",
                            }}
                        >
                            <SiderBrand collapsed={false} />
                            <Menu
                                theme="dark"
                                selectedKeys={[selectedKey]}
                                                               mode="inline"
                                items={items}
                                onClick={() => setCollapsed(true)}
                            />
                        </Sider>
                    </>
                )}

                <Layout style={isQV4 ? { flex: 1, overflow: 'hidden', minHeight: 0 } : {}}>
                    <div className='sticky top-0 left-0 z-99'>
                        <Header
                            style={{
                                padding: isMobile ? "0 10px" : "0 20px",
                                width: "100%",
                                height: 56,
                                lineHeight: "56px",
                                background: "#0e1013",
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: isQV4 ? 0 : 12,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                {isMobile && (
                                    <Button
                                        type="text"
                                        icon={<MenuOutlined style={{ fontSize: 18 }} />}
                                        onClick={() => setCollapsed(false)}
                                        style={{ color: "white" }}
                                    />
                                )}
                                <span style={{
                                    width: 8, height: 8, borderRadius: 2, background: "#e11d2e",
                                    display: "inline-block", flexShrink: 0, transform: "rotate(45deg)",
                                }} />
                                <h1 style={{
                                    margin: 0, fontSize: isMobile ? 15 : 17, fontWeight: 700,
                                    letterSpacing: 0.3, whiteSpace: "nowrap", overflow: "hidden",
                                    textOverflow: "ellipsis", color: "#f1f5f9",
                                }}>
                                    {currentHeader}
                                </h1>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginRight: pageDrawerWidth, transition: "margin-right 0.15s" }}>
                                {can(PERMISSIONS.INTERNAL_CHAT_USE) && Number(authState?.user?.internal_chat_enabled) === 1 && <InternalChatLauncher />}
                                {templateCredit !== null && (() => {
                                    const creditColor = templateCredit >= 10 ? "#52c41a" : templateCredit >= 5 ? "#faad14" : "#ff4d4f";
                                    const creditBg   = templateCredit >= 10 ? "rgba(82,196,26,0.12)" : templateCredit >= 5 ? "rgba(250,173,20,0.12)" : "rgba(255,77,79,0.12)";
                                    return (
                                        <AntTooltip title="Template credit balance" placement="bottom">
                                            <div
                                            onClick={fetchCredit}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 5,
                                                cursor: creditLoading ? "wait" : "pointer",
                                                backgroundColor: creditBg,
                                                border: `1.5px solid ${creditColor}`,
                                                borderRadius: 8,
                                                alignSelf: "stretch",
                                                padding: "0 12px 0 10px",
                                                opacity: creditLoading ? 0.6 : 1,
                                                animation: creditLoading ? "credit-pulse 0.8s ease-in-out infinite" : "none",
                                                transition: "opacity 0.2s",
                                            }}>
                                                <WalletOutlined style={{
                                                    fontSize: 14,
                                                    color: creditColor,
                                                    animation: creditLoading ? "credit-spin 1s linear infinite" : "none",
                                                }} />
                                                <span style={{ fontSize: 12, fontWeight: 700, color: creditColor, lineHeight: 1 }}>
                                                    ${templateCredit.toFixed(2)}
                                                </span>
                                            </div>
                                        </AntTooltip>
                                    );
                                })()}
                                {can(PERMISSIONS.APPROVALS_VIEW) && <ApprovalCenter />}
                                <AntTooltip
                                    placement="left"
                                    title={!isSrmSocketConnected
                                        ? (isNetworkOnline ? "Reconnecting to SRM — WATI relay status cannot be verified" : "Browser is offline — WATI relay status cannot be verified")
                                        : isWatiConnected
                                        ? "WhatsApp service connected through WATI"
                                        : `WhatsApp service ${relayStatus?.status || "status unknown"} through WATI${relayStatus?.reason ? ` — ${relayStatus.reason}` : ""}`}
                                >
                                    <div style={{ position: "relative", cursor: "default", lineHeight: 0 }}>
                                        <WhatsAppOutlined style={{ fontSize: 24, color: relayIndicator.color, animation: relayIndicator.pulse ? "credit-pulse 0.8s ease-in-out infinite" : "none" }} />
                                        <span style={{
                                            position: "absolute", bottom: -1, right: -3,
                                            width: 9, height: 9, borderRadius: "50%",
                                            background: relayIndicator.color,
                                            border: "2px solid #0e1013",
                                            display: "block",
                                        }} />
                                    </div>
                                </AntTooltip>
                                {(() => {
                                    const status = watiPresence?.status || null;
                                    const cfg = !isSrmSocketConnected
                                        ? {
                                            color: "#ff4d4f",
                                            pulse: isNetworkOnline,
                                            label: isNetworkOnline
                                                ? "Reconnecting to SRM — your WATI session cannot be verified"
                                                : "Browser is offline — your WATI session cannot be verified",
                                        }
                                        : ({
                                        connecting: { color: "#faad14", pulse: true, label: "Connecting your WATI session" },
                                        connected: { color: "#52c41a", pulse: false, label: "Your WATI session is online" },
                                        retrying: { color: "#faad14", pulse: true, label: "Reconnecting your WATI session" },
                                        waiting_for_token: { color: "#faad14", pulse: true, label: "Refreshing your WATI session" },
                                        failed: { color: "#ff4d4f", pulse: false, label: "Your WATI session failed" },
                                        disconnected: { color: "#8c8c8c", pulse: false, label: "Your WATI session is offline" },
                                    }[status] || { color: "#8c8c8c", pulse: false, label: "Your WATI session status is unknown" });
                                    const tooltipTitle = status === "failed" && watiPresence?.last_error
                                        ? `${cfg.label} — ${watiPresence.last_error}`
                                        : cfg.label;
                                    const onlineAgents = presenceAgents.filter(a => a.online || a.isBot);
                                    const offlineAgents = presenceAgents.filter(a => !(a.online || a.isBot));
                                    const sectionLabel = (text) => {
                                        const isOnline = text === "ONLINE";
                                        return (
                                            <div key={`__sec_${text}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 4px 2px" }}>
                                                <div style={{ flex: 1, height: 1, background: isOnline ? "#274d2e" : "#2a3942" }} />
                                                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: isOnline ? "#52c41a" : "#8696a0", whiteSpace: "nowrap" }}>{text}</span>
                                                <div style={{ flex: 1, height: 1, background: isOnline ? "#274d2e" : "#2a3942" }} />
                                            </div>
                                        );
                                    };
                                    const agentRow = (a) => (
                                        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", cursor: "default" }}>
                                            <div style={{ position: "relative", flexShrink: 0 }}>
                                                <Avatar size={26} style={{ backgroundColor: getAgentColor(a.fullName), fontSize: 10, fontWeight: 700, outline: "2px solid rgba(255,255,255,0.18)", outlineOffset: 1 }}>
                                                    {agentInitials(a.fullName)}
                                                </Avatar>
                                                <span style={{ position: "absolute", bottom: 0, right: -1, width: 7, height: 7, borderRadius: "50%", background: (a.online || a.isBot) ? "#52c41a" : "#bfbfbf", border: "1.5px solid #1f2c34" }} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, lineHeight: "1.3", color: "#e9edef", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.fullName}</div>
                                                {a.email && <div style={{ fontSize: 10, color: "#8696a0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.email}</div>}
                                            </div>
                                        </div>
                                    );
                                    return (
                                        <Dropdown
                                            trigger={["click"]}
                                            placement="bottomRight"
                                            getPopupContainer={() => document.body}
                                            onOpenChange={open => {
                                                if (!open) return;
                                                setPresenceAgentsLoading(true);
                                                // Live, not the shared cache — the whole point is diagnosing a
                                                // real-time mismatch, not seeing whatever another dropdown
                                                // happened to fetch a minute ago.
                                                _fetchAgents(true).then(list => { setPresenceAgents(list); setPresenceAgentsLoading(false); });
                                            }}
                                            popupRender={() => (
                                                <div style={{ maxHeight: "calc(100vh - 32px)", overflowY: "auto", minWidth: 220, borderRadius: 8, background: "#1f2c34", border: "1px solid #2a3942", boxShadow: "0 6px 20px rgba(0,0,0,0.5)", padding: 6 }}>
                                                    <div style={{ fontSize: 10, color: "#8696a0", padding: "2px 6px 6px", fontWeight: 600 }}>WATI AGENTS (LIVE)</div>
                                                    {presenceAgentsLoading ? (
                                                        <div style={{ textAlign: "center", padding: 12 }}><Spin size="small" /></div>
                                                    ) : presenceAgents.length === 0 ? (
                                                        <div style={{ fontSize: 12, color: "#8696a0", padding: "6px" }}>No agents found.</div>
                                                    ) : (
                                                        <>
                                                            {onlineAgents.length > 0 && sectionLabel("ONLINE")}
                                                            {onlineAgents.map(agentRow)}
                                                            {offlineAgents.length > 0 && sectionLabel("OFFLINE")}
                                                            {offlineAgents.map(agentRow)}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        >
                                            <AntTooltip placement="left" title={tooltipTitle}>
                                                <div style={{ position: "relative", cursor: "pointer", lineHeight: 0 }}>
                                                    <UserSwitchOutlined style={{ fontSize: 22, color: cfg.color }} />
                                                    <span style={{
                                                        position: "absolute", bottom: -1, right: -3,
                                                        width: 9, height: 9, borderRadius: "50%",
                                                        background: cfg.color,
                                                        border: "2px solid #0e1013",
                                                        display: "block",
                                                        animation: cfg.pulse ? "credit-pulse 0.8s ease-in-out infinite" : "none",
                                                    }} />
                                                </div>
                                            </AntTooltip>
                                        </Dropdown>
                                    );
                                })()}
                                <Dropdown
                                    className='cursor-pointer'
                                    menu={{ items: dropdown_items, onClick: onMenuClick }}
                                    placement="bottomLeft"
                                >
                                    <AntTooltip placement='left' title={`Logged in as ${userState?.user?.name || "Unknown User"}`}>
                                        <Avatar
                                        size={isMobile ? "default" : "large"}
                                        src={resolvePfpUrl(userState?.user?.pfp)}
                                        style={{ backgroundColor: '#fde3cf', color: '#f56a00', marginRight: 8 }}
                                    >
                                        {stringAvatar(userState?.user?.name || "Unknown User")}
                                        </Avatar>
                                    </AntTooltip>
                                </Dropdown>
                            </div>
                        </Header>
                    </div>
                    <Content style={isQV4
                        ? { flex: 1, overflow: 'hidden', minHeight: 0, margin: 0 }
                        : { margin: isMobile ? '0 4px' : '0 16px' }
                    }>
                        <div
                            style={isQV4
                                ? { height: '100%', overflow: 'hidden' }
                                : { padding: isMobile ? 8 : 24, minHeight: 360, background: colorBgContainer, borderRadius: borderRadiusLG }
                            }
                        >
                            {/* Inner boundary: page-chunk loads keep the nav shell visible */}
                            <Suspense fallback={
                                <div style={{ height: "100%", minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Spin size="large" />
                                </div>
                            }>
                                <Outlet context={{ setPageDrawerWidth }} />
                            </Suspense>
                        </div>
                    </Content>
                    {!isQV4 && (
                        <Footer style={{ textAlign: 'center', fontSize: isMobile ? 10 : 11, color: "#8c8c8c", padding: isMobile ? "12px 16px" : "14px 16px", background: "transparent" }}>
                            UzdPart Used Spare Parts Trading LLC © {new Date().getFullYear()} · {siteConfig.name} v{siteConfig.version} · build {siteConfig.build_no}
                        </Footer>
                    )}
                </Layout>
            </Layout>
            {LogoutDialog}
            {can(PERMISSIONS.SETTINGS_VIEW) && <SettingsModal
                open_settings={globalSettingsOpen}
                handleCloseSettingsModal={() => setGlobalSettingsOpen(false)}
            />}
            <UserSettingsModal
                open_settings={userSettingsOpen}
                handleCloseSettingsModal={() => setUserSettingsOpen(false)}
                authState={authState}
            />
            <ToastContainer />
        </ConfigProvider>
    );
};

export default DrawerNew;
